import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, GraduationCap, Sparkles, ArrowRight, Download, Lock } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const IMG = {
  hero: "https://static.prod-images.emergentagent.com/jobs/10ccecec-b8c9-46bc-acaf-33191ffb3202/images/fc3506f8fbf112ba778036907c88580c25cc9ec9243d9db7acb47f82bbf8e262.jpeg",
  bts: "https://images.unsplash.com/photo-1631730486572-226d1f595b68?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTF8MHwxfHNlYXJjaHwzfHxiZWF1dHklMjBhZXN0aGV0aWNzJTIwY29zbWV0aWNzJTIwZWxlZ2FudHxlbnwwfHx8fDE3ODY2NTYyMDR8MA&ixlib=rb-4.1.0&q=85",
  cap: "https://images.unsplash.com/photo-1596704017254-9b121068fb31?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHwxfHxlbGVnYW50JTIwc3R1ZGVudCUyMGxlYXJuaW5nJTIwY29tcHV0ZXIlMjBjb3NtZXRpY3N8ZW58MHx8fHwxNzg2NjU2MjA0fDA&ixlib=rb-4.1.0&q=85",
  bp: "https://images.unsplash.com/photo-1583209814683-c023dd293cc6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTF8MHwxfHNlYXJjaHwxfHxiZWF1dHklMjBhZXN0aGV0aWNzJTIwY29zbWV0aWNzJTIwZWxlZ2FudHxlbnwwfHx8fDE3ODY2NTYyMDR8MA&ixlib=rb-4.1.0&q=85",
};

const CATEGORIES = [
  {
    code: "BTS",
    title: "BTS Métiers de l'Esthétique",
    text: "Cosmétologie, biologie, management de l'institut, techniques esthétiques avancées.",
    img: IMG.bts,
    span: "md:col-span-7 md:row-span-2",
    height: "min-h-[420px] md:min-h-[520px]",
  },
  {
    code: "CAP",
    title: "CAP Esthétique",
    text: "Soins du visage, épilation, maquillage, technologie des appareils.",
    img: IMG.cap,
    span: "md:col-span-5",
    height: "min-h-[250px]",
  },
  {
    code: "BP",
    title: "BP Esthétique Cosmétique",
    text: "Perfectionnement, vente-conseil, gestion et pratiques professionnelles.",
    img: IMG.bp,
    span: "md:col-span-5",
    height: "min-h-[250px]",
  },
];

export default function Landing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [query, setQuery] = useState("");
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/stats").then(({ data }) => setStats(data)).catch(() => {});
  }, []);

  const search = (e) => {
    e?.preventDefault();
    if (user) navigate(`/ressources?q=${encodeURIComponent(query)}`);
    else navigate(`/inscription?q=${encodeURIComponent(query)}`);
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <Navbar />

      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={IMG.hero} alt="Esthétique" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#FDFBF7]/85 sm:bg-transparent" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#FDFBF7] via-[#FDFBF7]/94 to-[#FDFBF7]/55" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-32">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="max-w-2xl"
          >
            <img
              src="https://static.prod-images.emergentagent.com/jobs/10ccecec-b8c9-46bc-acaf-33191ffb3202/images/4ae303166a464beaa98eee80619c44d864bb9c7d6f38659351ba37bc82cffa83.jpeg"
              alt="Logo Wonderpro"
              data-testid="hero-logo"
              className="w-44 h-44 object-contain rounded-3xl mb-4 shadow-[0_8px_30px_rgba(183,110,121,0.12)]"
            />
            <p className="text-xs uppercase tracking-[0.32em] text-[#B76E79] mb-6">
              Ressources pédagogiques
            </p>
            <h1 className="font-display text-5xl lg:text-6xl tracking-tight leading-none text-[#2C2627] mb-6">
              Esthétique &amp; Cosmétique
              <span className="block text-[#B76E79] italic">tous vos supports de cours</span>
            </h1>
            <p className="text-base leading-relaxed text-[#5E5356] max-w-xl mb-10">
              PDF, vidéos, audios, images et pages HTML pour les filières BTS, CAP et BP.
              Inscrivez-vous pour accéder à la bibliothèque complète et télécharger vos supports.
            </p>

            <form onSubmit={search} className="flex flex-col sm:flex-row gap-3 max-w-xl mb-8">
              <div className="relative flex-1">
                <Search
                  strokeWidth={1.5}
                  className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#B76E79]"
                />
                <Input
                  data-testid="hero-search-input"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Rechercher un cours, un module, un thème…"
                  className="h-14 pl-12 rounded-full bg-white/80 backdrop-blur-md border-[#E8D7D0] focus-visible:ring-[#B76E79] text-base"
                />
              </div>
              <Button
                type="submit"
                data-testid="hero-search-btn"
                className="h-14 px-8 rounded-full bg-[#B76E79] hover:bg-[#A25D67] text-white text-base"
              >
                Rechercher
              </Button>
            </form>

            <div className="flex flex-wrap gap-4">
              <Button
                data-testid="hero-register-btn"
                onClick={() => navigate(user ? "/ressources" : "/inscription")}
                className="h-12 px-8 rounded-full bg-[#2C2627] hover:bg-[#463d3f] text-white"
              >
                {user ? "Accéder aux ressources" : "Créer mon compte étudiant"}
                <ArrowRight strokeWidth={1.5} className="w-4 h-4 ml-2" />
              </Button>
              {!user && (
                <Button
                  variant="outline"
                  data-testid="hero-login-btn"
                  onClick={() => navigate("/connexion")}
                  className="h-12 px-8 rounded-full border-[#B76E79] text-[#B76E79] hover:bg-[#F5E6E8]"
                >
                  J'ai déjà un compte
                </Button>
              )}
            </div>
          </motion.div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-28">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-14">
          <div className="max-w-xl">
            <p className="text-sm uppercase tracking-[0.2em] text-[#B76E79] mb-4">Les 3 rubriques</p>
            <h2 className="font-display text-3xl lg:text-4xl tracking-tight leading-tight text-[#2C2627]">
              Choisissez votre filière
            </h2>
          </div>
          {stats && (
            <div className="flex gap-10" data-testid="landing-stats">
              <div>
                <p className="font-display text-4xl text-[#B76E79]">{stats.total_resources}</p>
                <p className="text-xs uppercase tracking-[0.2em] text-[#5E5356]">Ressources</p>
              </div>
              <div>
                <p className="font-display text-4xl text-[#B76E79]">{stats.total_students}</p>
                <p className="text-xs uppercase tracking-[0.2em] text-[#5E5356]">Étudiants</p>
              </div>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
          {CATEGORIES.map((cat, i) => (
            <motion.button
              key={cat.code}
              data-testid={`category-card-${cat.code}`}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.12 }}
              onClick={() => navigate(user ? `/ressources?category=${cat.code}` : "/inscription")}
              className={`${cat.span} ${cat.height} relative overflow-hidden rounded-3xl border border-[#E8D7D0] text-left group hover:-translate-y-1 transition-transform duration-300 ease-out`}
            >
              <img
                src={cat.img}
                alt={cat.title}
                className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#2C2627]/85 via-[#2C2627]/30 to-transparent" />
              <div className="relative h-full flex flex-col justify-end p-8 lg:p-10">
                <span className="inline-flex items-center gap-2 self-start rounded-full bg-white/90 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.2em] text-[#B76E79] mb-4">
                  <GraduationCap strokeWidth={1.5} className="w-4 h-4" />
                  {cat.code}
                  {stats ? ` · ${stats.categories[cat.code]}` : ""}
                </span>
                <h3 className="font-display text-2xl lg:text-3xl text-white mb-3">{cat.title}</h3>
                <p className="text-sm text-white/80 max-w-md leading-relaxed">{cat.text}</p>
                <span className="mt-6 inline-flex items-center gap-2 text-sm text-white">
                  Voir les supports
                  <ArrowRight strokeWidth={1.5} className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
                </span>
              </div>
            </motion.button>
          ))}
        </div>
      </section>

      <section className="border-y border-[#E8D7D0] bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 grid grid-cols-1 md:grid-cols-3 gap-12">
          {[
            { Icon: Lock, t: "Accès réservé", d: "Une inscription simple protège l'accès aux supports pédagogiques." },
            { Icon: Download, d: "Téléchargez PDF, vidéos, audios, images et pages HTML sans limite.", t: "Tous formats" },
            { Icon: Sparkles, t: "Contenu curaté", d: "Des supports classés par filière et par module, mis à jour régulièrement." },
          ].map(({ Icon, t, d }) => (
            <div key={t}>
              <Icon strokeWidth={1.5} className="w-7 h-7 text-[#B76E79] mb-5" />
              <h3 className="font-display text-xl lg:text-2xl text-[#2C2627] mb-3">{t}</h3>
              <p className="text-sm text-[#5E5356] leading-relaxed">{d}</p>
            </div>
          ))}
        </div>
      </section>

      <footer className="max-w-7xl mx-auto px-6 lg:px-10 py-12 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <span className="font-logo text-xl text-[#B76E79]">Wonderpro</span>
        <p className="text-xs text-[#5E5356]">
          © {new Date().getFullYear()} Wonderpro — Ressources Esthétique &amp; Cosmétique
        </p>
      </footer>
    </div>
  );
}
